import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Input from './Input';
import Select from './Select';
import Button from './Button';
import { Currency } from '../../types';

interface CurrencyFormProps {
  onSubmit: (data: Omit<Currency, 'id' | 'createdAt' | 'updatedAt' | 'lastUpdated'>) => void;
  initialData?: Currency;
  onCancel: () => void;
}

interface CurrencyFormData {
  code: string;
  name: string;
  symbol: string;
  exchangeRateToEGP: number;
  isActive: boolean;
}

const CurrencyForm: React.FC<CurrencyFormProps> = ({ onSubmit, initialData, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<CurrencyFormData>({
    defaultValues: initialData ? {
      code: initialData.code,
      name: initialData.name,
      symbol: initialData.symbol,
      exchangeRateToEGP: initialData.exchangeRateToEGP,
      isActive: initialData.isActive
    } : {
      isActive: true,
      exchangeRateToEGP: 1
    }
  });

  useEffect(() => {
    if (initialData) {
      reset({
        code: initialData.code,
        name: initialData.name,
        symbol: initialData.symbol,
        exchangeRateToEGP: initialData.exchangeRateToEGP,
        isActive: initialData.isActive
      });
    }
  }, [initialData, reset]);

  const handleFormSubmit = (data: CurrencyFormData) => {
    onSubmit(data);
    if (!initialData) {
      reset();
    }
  };

  const statusOptions = [
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' }
  ];

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Currency Code"
          type="text"
          placeholder="e.g., USD, EUR, GBP"
          required
          {...register('code', { 
            required: 'Currency code is required',
            pattern: {
              value: /^[A-Z]{3}$/,
              message: 'Currency code must be 3 uppercase letters (e.g., USD)'
            }
          })}
          error={errors.code?.message}
        />

        <Input
          label="Currency Name"
          type="text"
          placeholder="e.g., US Dollar, Euro"
          required
          {...register('name', { required: 'Currency name is required' })}
          error={errors.name?.message}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Currency Symbol"
          type="text"
          placeholder="e.g., $, €, £"
          required
          {...register('symbol', { required: 'Currency symbol is required' })}
          error={errors.symbol?.message}
        />

        <Input
          label="Exchange Rate to EGP"
          type="number"
          step="0.0001"
          placeholder="1.0000"
          required
          {...register('exchangeRateToEGP', { 
            required: 'Exchange rate is required',
            valueAsNumber: true,
            min: { value: 0.0001, message: 'Exchange rate must be greater than 0' }
          })}
          error={errors.exchangeRateToEGP?.message}
        />
      </div>

      <Select
        label="Status"
        {...register('isActive', { 
          setValueAs: (value) => value === 'true'
        })}
        options={statusOptions}
        error={errors.isActive?.message}
      />

      <div className="flex justify-end space-x-3 pt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
        >
          {initialData ? 'Update Currency' : 'Add Currency'}
        </Button>
      </div>
    </form>
  );
};

export default CurrencyForm;

